import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import * as parentService from '../services/parentService';

// Re-export types from parentService
export type {
  ParentChild,
  ChildActivity,
  ChildPhoto,
  AttendanceSummary,
  ParentDashboardData
} from '../services/parentService';

// Query keys
const QUERY_KEYS = {
  dashboard: ['parent', 'dashboard'] as const,
  children: ['parent', 'children'] as const,
  childDetails: (id: string) => ['parent', 'children', id] as const,
  childActivities: (id: string) => ['parent', 'children', id, 'activities'] as const,
  childPhotos: (id: string) => ['parent', 'children', id, 'photos'] as const,
  childDailyReport: (id: string, date?: string) => ['parent', 'children', id, 'daily-report', date] as const,
  childAttendance: (id: string) => ['parent', 'children', id, 'attendance'] as const,
  notifications: ['parent', 'notifications'] as const,
  childSummary: (id: string) => ['parent', 'children', id, 'summary'] as const,
};

// Get parent dashboard data using admin endpoints
export const useParentDashboard = () => {
  return useQuery({
    queryKey: QUERY_KEYS.dashboard,
    queryFn: () => parentService.getParentDashboardData(),
    refetchInterval: 30000, // Refresh every 30 seconds
    staleTime: 15000, // Consider stale after 15 seconds
  });
};

// Get parent's children
export const useParentChildren = () => {
  return useQuery({
    queryKey: QUERY_KEYS.children,
    queryFn: async () => {
      const response = await parentService.getParentChildren();
      return response.items;
    },
  });
};

// Get child profile
export const useChildProfile = (childId: string | undefined, enabled = true) => {
  return useQuery({
    queryKey: QUERY_KEYS.childDetails(childId!),
    queryFn: () => parentService.getChildDetails(childId!),
    enabled: enabled && !!childId,
  });
};

// Alias for backward compatibility
export const useChildDetails = useChildProfile;

// Get child activities
export const useChildActivities = (
  childId: string | undefined,
  params?: {
    date?: string;
  },
  enabled = true
) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.childActivities(childId!), params],
    queryFn: async () => {
      if (!childId) return [];
      const response = await parentService.getChildActivities(childId, params);
      return response.items;
    },
    enabled: enabled && !!childId,
  });
};

// Get parent child activities (alternative endpoint with pagination)
export const useParentChildActivities = (
  childId: string | undefined,
  params?: {
    start_date?: string;
    end_date?: string;
    page?: number;
    per_page?: number;
  },
  enabled = true
) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.childActivities(String(childId)), 'paginated', params],
    queryFn: async () => {
      if (!childId) return { items: [], pagination: undefined };
      return parentService.getParentChildActivities(childId, params);
    },
    enabled: enabled && !!childId,
  });
};

// Get child photos
export const useChildPhotos = (
  childId: string | undefined,
  params?: {
    page?: number;
    per_page?: number;
    search?: string;
    activity_type?: string;
    start_date?: string;
    end_date?: string;
  },
  enabled = true
) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.childPhotos(childId!), params],
    queryFn: async () => {
      if (!childId) return [];
      const response = await parentService.getChildPhotos(childId, params);
      return response.items;
    },
    enabled: enabled && !!childId,
  });
};

// Get child daily report
export const useChildDailyReport = (
  childId: string | undefined,
  date?: string,
  enabled = true
) => {
  return useQuery({
    queryKey: QUERY_KEYS.childDailyReport(childId!, date),
    queryFn: () => {
      if (!childId) return null;
      return parentService.getChildDailyReport(childId, { date });
    },
    enabled: enabled && !!childId,
  });
};

// Get child attendance summary
export const useChildAttendanceSummary = (
  childId: string | undefined,
  params?: {
    month?: number;
    year?: number;
  },
  enabled = true
) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.childAttendance(childId!), 'summary', params],
    queryFn: () => {
      if (!childId) return null;
      return parentService.getChildAttendanceSummary(childId, params);
    },
    enabled: enabled && !!childId,
  });
};

// Get parent notifications using real API
export const useParentNotifications = (params?: {
  page?: number;
  per_page?: number;
  unread_only?: boolean;
}) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.notifications, params],
    queryFn: () => parentService.getParentNotificationsFromAPI(params),
    refetchInterval: 30000, // Refresh every 30 seconds for real-time updates
    staleTime: 15000, // Consider stale after 15 seconds
  });
};

// Mark notification as read using real API
export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) =>
      parentService.markNotificationAsReadAPI(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications });
      notifications.show({
        title: 'Notification Updated',
        message: 'Notification marked as read',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to mark notification as read',
        color: 'red',
      });
    },
  });
};

// Send message to teacher
export const useSendTeacherMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      recipient_id: string;
      subject: string;
      message: string;
    }) => parentService.sendParentMessage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parent', 'messages'] });
      notifications.show({
        title: 'Message Sent',
        message: 'Your message has been sent to the teacher',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to send message. Please try again.',
        color: 'red',
      });
    },
  });
};

// Request photo download
export const useRequestPhotoDownload = () => {
  return useMutation({
    mutationFn: (photoIds: string[]) =>
      parentService.requestPhotoDownload(photoIds),
    onSuccess: (data) => {
      // Open download URL in new tab
      window.open(data.download_url, '_blank');
      notifications.show({
        title: 'Download Started',
        message: 'Your photos are being downloaded',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to download photos. Please try again.',
        color: 'red',
      });
    },
  });
};

// Get notification preferences using real API
export const useNotificationPreferences = () => {
  return useQuery({
    queryKey: ['parent', 'notification-preferences'],
    queryFn: () => parentService.getUserNotificationPreferences(),
    refetchInterval: 300000, // Refresh every 5 minutes
  });
};

// Update notification preferences using real API
export const useUpdateNotificationPreferences = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (preferences: {
      daily_reports: boolean;
      activity_updates: boolean;
      photo_uploads: boolean;
      safety_alerts: boolean;
      teacher_messages: boolean;
      schedule_changes: boolean;
      email_notifications: boolean;
      sms_notifications: boolean;
      push_notifications: boolean;
      frequency?: string;
    }) => parentService.updateUserNotificationPreferences(preferences),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parent', 'notification-preferences'] });
      notifications.show({
        title: 'Preferences Updated',
        message: 'Your notification preferences have been saved',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to update notification preferences',
        color: 'red',
      });
    },
  });
};

// Download daily report
export const useDownloadDailyReport = () => {
  return useMutation({
    mutationFn: async ({ childId, date }: { childId: string; date: string }) => {
      // This would typically call a specific download endpoint
      // For now, we'll simulate the download functionality
      const report = await parentService.getChildDailyReport(childId, { date });

      // Create a downloadable file
      const dataStr = JSON.stringify(report, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);

      // Create temporary download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `daily-report-${date}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      URL.revokeObjectURL(url);

      return { success: true };
    },
    onSuccess: () => {
      notifications.show({
        title: 'Download Started',
        message: 'Daily report download has started',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Download Failed',
        message: 'Failed to download daily report. Please try again.',
        color: 'red',
      });
    },
  });
};

// Legacy support - redirect to new functions
export const useChildDailyReports = (childId?: string) => {
  const today = new Date().toISOString().split('T')[0];
  return useChildDailyReport(childId, today);
};

export const useChildAttendance = (childId?: string) => {
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();
  return useChildAttendanceSummary(childId, { month: currentMonth, year: currentYear });
};

// New hooks using admin endpoints for parent dashboard integration

// Get child activities from detections endpoint
export const useChildActivitiesFromDetections = (
  childId: string | undefined,
  params?: { date?: string; limit?: number }
) => {
  return useQuery({
    queryKey: ['parent', 'children', childId, 'activities-detections', params],
    queryFn: () => parentService.getChildActivitiesFromDetections(childId!, params),
    enabled: !!childId,
    refetchInterval: 30000, // Refresh every 30 seconds
  });
};

// Get child summary using student endpoint
export const useChildSummaryFromStudent = (childId: string | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.childSummary(childId!),
    queryFn: () => parentService.getChildSummary(childId!),
    enabled: !!childId,
    refetchInterval: 60000, // Refresh every minute
  });
};

// Get children from students endpoint (for admin access)
export const useParentChildrenFromStudents = () => {
  return useQuery({
    queryKey: ['parent', 'children-from-students'],
    queryFn: async () => {
      const dashboardData = await parentService.getParentDashboardData();
      return dashboardData.children;
    },
    refetchInterval: 60000, // Refresh every minute
  });
};

// Enhanced photo gallery hooks
export const useChildPhotosFromDetections = (
  childId: string | undefined,
  params?: { date?: string; limit?: number; activity_type?: string }
) => {
  return useQuery({
    queryKey: ['parent', 'children', childId, 'photos-detections', params],
    queryFn: () => parentService.getChildPhotosFromDetections(childId!, params),
    enabled: !!childId,
    refetchInterval: 60000, // Refresh every minute
  });
};

// Enhanced activity timeline hooks
export const useEnhancedChildActivities = (
  childId: string | undefined,
  params?: {
    date?: string;
    start_date?: string;
    end_date?: string;
    activity_type?: string;
    location?: string;
    verified?: boolean;
    limit?: number;
    page?: number;
  }
) => {
  return useQuery({
    queryKey: ['parent', 'children', childId, 'activities-enhanced', params],
    queryFn: () => parentService.getEnhancedChildActivities(childId!, params),
    enabled: !!childId,
    refetchInterval: 30000, // Refresh every 30 seconds
    keepPreviousData: true, // Keep previous data while fetching new page
  });
};

// Daily report from data hooks
export const useChildDailyReportFromData = (
  childId: string | undefined,
  date?: string
) => {
  return useQuery({
    queryKey: ['parent', 'children', childId, 'daily-report-data', date],
    queryFn: () => parentService.getChildDailyReportFromData(childId!, date),
    enabled: !!childId,
    refetchInterval: 300000, // Refresh every 5 minutes
  });
};

// Activity statistics hook
export const useChildActivityStats = (
  childId: string | undefined,
  dateRange?: { start_date: string; end_date: string }
) => {
  return useQuery({
    queryKey: ['parent', 'children', childId, 'activity-stats', dateRange],
    queryFn: async () => {
      if (!childId) return null;

      const { activities } = await parentService.getEnhancedChildActivities(childId, {
        start_date: dateRange?.start_date,
        end_date: dateRange?.end_date,
        limit: 1000, // Get all activities for stats
      });

      // Calculate statistics
      const totalActivities = activities.length;
      const activityTypes = activities.reduce((acc: any, activity) => {
        const activityType = activity.activity_type || 'unknown';
        acc[activityType] = (acc[activityType] || 0) + 1;
        return acc;
      }, {});

      const verifiedActivities = activities.filter(a => a.verified).length;
      const safetyIncidents = activities.filter(a =>
        a.activity_type?.includes('unsafe') || a.tags?.includes('safety')
      ).length;

      const averageConfidence = activities.reduce((sum, a) => sum + (a.confidence || 0), 0) / totalActivities || 0;

      return {
        totalActivities,
        activityTypes,
        verifiedActivities,
        verificationRate: totalActivities > 0 ? (verifiedActivities / totalActivities) * 100 : 0,
        safetyIncidents,
        averageConfidence: Math.round(averageConfidence * 100),
        mostCommonActivity: Object.entries(activityTypes).sort(([,a], [,b]) => (b as number) - (a as number))[0]?.[0] || 'none',
      };
    },
    enabled: !!childId,
    refetchInterval: 300000, // Refresh every 5 minutes
  });
};

// Enhanced communication and notification hooks

// Get available teachers for communication
export const useAvailableTeachers = () => {
  return useQuery({
    queryKey: ['parent', 'teachers'],
    queryFn: () => parentService.getAvailableTeachers(),
    refetchInterval: 300000, // Refresh every 5 minutes
  });
};

// Send parent-teacher message
export const useSendParentTeacherMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      teacher_id?: string;
      subject: string;
      message: string;
      child_id?: string;
    }) => parentService.sendParentTeacherMessage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parent', 'messages'] });
      notifications.show({
        title: 'Message Sent',
        message: 'Your message has been sent to the teacher',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to send message. Please try again.',
        color: 'red',
      });
    },
  });
};

// Get parent-teacher messages
export const useParentTeacherMessages = (params?: {
  teacher_id?: string;
  child_id?: string;
  limit?: number;
}) => {
  return useQuery({
    queryKey: ['parent', 'messages', params],
    queryFn: () => parentService.getParentTeacherMessages(params),
    refetchInterval: 60000, // Refresh every minute
  });
};

// Get safety alerts from detections
export const useSafetyAlerts = (
  childId?: string,
  params?: { date?: string; limit?: number }
) => {
  return useQuery({
    queryKey: ['parent', 'safety-alerts', childId, params],
    queryFn: () => parentService.getSafetyAlertsFromDetections(childId, params),
    enabled: !!childId,
    refetchInterval: 30000, // Refresh every 30 seconds for safety alerts
  });
};

// Get unread notification count
export const useUnreadNotificationCount = () => {
  return useQuery({
    queryKey: ['parent', 'notifications', 'unread-count'],
    queryFn: async () => {
      const { notifications: notifs } = await parentService.getParentNotificationsFromAPI({
        unread_only: true,
        per_page: 100,
      });
      return notifs.length;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });
};

// Real-time notification polling hook
export const useRealTimeNotifications = () => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['parent', 'notifications', 'realtime'],
    queryFn: async () => {
      const { notifications: notifs } = await parentService.getParentNotificationsFromAPI({
        per_page: 5, // Get latest 5 notifications
      });
      return notifs;
    },
    refetchInterval: 15000, // Very frequent updates for real-time feel
    onSuccess: (newNotifications) => {
      // Invalidate main notifications query to update the full list
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications });

      // Show toast for new urgent notifications
      newNotifications.forEach((notification: any) => {
        if (notification.priority === 'high' && !notification.read) {
          notifications.show({
            title: notification.title,
            message: notification.message,
            color: notification.type === 'safety_alert' ? 'red' : 'blue',
            autoClose: false, // Keep urgent notifications visible
          });
        }
      });
    },
  });
};
